import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, HostListener, inject, effect, ChangeDetectorRef, signal, computed, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as echarts from 'echarts';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { Subscription, timer } from 'rxjs';
import { ExternalLiquidityService } from '../../services/external-liquidity.service';
import { Web3Service } from '../../services/web3.service';
import { ActivityService } from '../../services/activity.service';
import { formatSubscript, formatPlain } from '../../utils/number.util';
import { ethers } from 'ethers';
import { POSITION_MANAGER_ABI } from '../../config/contracts.config';
import { Pool, Position, TickMath } from '@uniswap/v3-sdk';
import { Token as UniToken } from '@uniswap/sdk-core';
import JSBI from 'jsbi';

import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { HistoryComponent } from '../history/history.component';
import { SparklineChartComponent } from '../sparkline-chart/sparkline-chart.component';

@Component({
  selector: 'app-portfolio',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HistoryComponent, SparklineChartComponent],
  templateUrl: './portfolio.component.html'
})
export class PortfolioComponent implements OnInit, AfterViewInit, OnDestroy {
  appState = inject(AppStateService);
  authService = inject(AuthService);
  http = inject(HttpClient);
  activityService = inject(ActivityService);
  private externalLiquidityService = inject(ExternalLiquidityService);

  @ViewChild('performanceChart') performanceChartRef!: ElementRef;
  @ViewChild('allocationChart') allocationChartRef!: ElementRef;

  private performanceChartInstance: echarts.ECharts | null = null;
  private allocationChartInstance: echarts.ECharts | null = null;

  activeTimeframe = '30D';
  activeTab = 'Assets';
  searchQuery = signal('');
  hideZeroBalances = signal(false);


  // Modal States
  isRemoveModalOpen = false;
  isClaimModalOpen = false;
  selectedPosition: any = null;
  removePercentage = 50;

  isRemovingLiquidity = false;
  isClaimingFees = false;

  // Unstake Modal state
  isUnstakeModalOpen = false;
  isLoadingPid = false;
  isClaimRewardModalOpen = false;
  isUnstaking = false;
  isClaimingReward = false;
  selectedFarmPid: number | null = null;
  liveRewardAmount: string = '0.00';
  formatSubscript = formatSubscript;
  isLoadingLiveReward = false;

  liveFeeAmount0: string = '0.00';
  liveFeeAmount1: string = '0.00';
  liveFeeUsd0: number = 0;
  liveFeeUsd1: number = 0;
  liveFeeUsdFormatted0: string = '0.00';
  liveFeeUsdFormatted1: string = '0.00';
  isLoadingLiveFees = false;
  rawFeeAmount0: number = 0;
  rawFeeAmount1: number = 0;
  liveToken0Amount: number | null = null;
  liveToken1Amount: number | null = null;

  get receiveAmount0(): string {
    if (!this.selectedPosition) return '0.00';
    const baseAmount = this.liveToken0Amount !== null ? this.liveToken0Amount : Number(this.selectedPosition.token0Amount);
    const amount = baseAmount * (this.removePercentage / 100);
    return this.formatSubscript(amount);
  }

  get receiveAmount1(): string {
    if (!this.selectedPosition) return '0.00';
    const baseAmount = this.liveToken1Amount !== null ? this.liveToken1Amount : Number(this.selectedPosition.token1Amount);
    const amount = baseAmount * (this.removePercentage / 100);
    return this.formatSubscript(amount);
  }

  get feeAmount0(): string {
    return this.liveFeeAmount0;
  }

  get feeAmount1(): string {
    return this.liveFeeAmount1;
  }

  get hasNoRewards(): boolean {
    return parseFloat(this.liveRewardAmount) <= 0;
  }

  get hasNoFees(): boolean {
    return this.rawFeeAmount0 <= 0 && this.rawFeeAmount1 <= 0;
  }

  private web3Service = inject(Web3Service);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  openAddLiquidityWithParams(pos: any) {
    this.router.navigate(['/liquidity/new'], {
      queryParams: {
        token1: pos.token0Symbol,
        token2: pos.token1Symbol,
        fee: pos.poolFee ? (Number(pos.poolFee) / 10000).toFixed(2) + '%' : '0.30%',
        minPrice: pos.minPriceRaw,
        maxPrice: pos.maxPriceRaw,
        isFullRange: pos.isFullRange,
        amount1: pos.token0Amount,
        amount2: pos.token1Amount,
        step: 2
      }
    });
  }

  constructor() {
    effect(() => {
      if (this.appState.isWalletConnected()) {
        setTimeout(() => {
          this.initCharts();
        }, 50); // slight delay to allow *ngIf to render DOM
      } else {
        if (this.performanceChartInstance) {
          this.performanceChartInstance.dispose();
          this.performanceChartInstance = null;
        }
        if (this.allocationChartInstance) {
          this.allocationChartInstance.dispose();
          this.allocationChartInstance = null;
        }
      }
    });

    effect(() => {
      const networkId = this.appState.selectedNetworkId();
      const isConnected = this.appState.isWalletConnected();
      if (this.authService.isAuthenticated() || isConnected) {
        untracked(() => {
          this.fetchGlobalMetrics();
          if (this.activeTab === 'Positions' || this.activeTab === 'NFTs') {
            this.loadUserPositions();
          }
        });
      } else {
        this.positions.set([]); 
      }
    });
  }

  getFeeTierLabel(fee: number): string {
    if (!fee) return '';
    return (fee / 10000).toString() + '%';
  }

  ngOnInit() {

    this.route.fragment.subscribe(fragment => {
      console.log('[DEBUG PORTFOLIO] route.fragment.subscribe triggered with fragment:', fragment);
      let nextTab = this.activeTab;
      if (fragment) {
        const matchingTab = this.tabs.find(t => t.toLowerCase() === fragment.toLowerCase());
        if (matchingTab) nextTab = matchingTab;
      }

      this.activeTab = nextTab;
      console.log('[DEBUG PORTFOLIO] activeTab set to:', this.activeTab, 'isWalletConnected:', this.appState.isWalletConnected());

      if (this.appState.isWalletConnected()) {
        untracked(() => {
          this.fetchGlobalMetrics();
        });
        if (this.activeTab === 'Assets') {
          setTimeout(() => this.initCharts(), 100);
        } else if (this.activeTab === 'Positions' || this.activeTab === 'NFTs') {
          console.log('[DEBUG PORTFOLIO] Calling loadUserPositions from route.fragment.subscribe!');
          this.loadUserPositions();
        }
      }
    });
  }

  trackByAsset(index: number, asset: any): string {
    return asset.address || asset.symbol;
  }

  timeframes = ['7D', '30D', '90D', '1Y', 'All'];
  public tabs = ['Assets', 'Positions', 'NFTs', 'Activity'];

  filteredAssets = computed(() => {
    if (!this.appState.isWalletConnected()) return [];

    const balances = this.appState.tokenBalances();
    const tokens = this.appState.tokens();

    // Compute raw assets
    let rawAssets = tokens.map(t => {
      const addressKey = t.address ? t.address.toLowerCase() : '';
      const balanceStr = balances[addressKey] || '0';
      const balance = parseFloat(balanceStr);
      const price = t.rate || 0;
      const value = balance * price;

      return {
        symbol: t.symbol,
        name: t.name,
        balance: balance,
        price: price,
        priceChange: t.change || 0,
        value: value,
        allocation: 0,
        iconUrl: t.logoUrl,
        color: t.color || '#888',
        address: t.address
      };
    }).filter(a => a.value < 1e15 && a.balance < 1e30); // Sanity check for MaxUint256 glitches

    const totalValue = rawAssets.reduce((sum, a) => sum + a.value, 0);

    rawAssets.forEach(a => {
      a.allocation = totalValue > 0 ? Number(((a.value / totalValue) * 100).toFixed(1)) : 0;
    });

    // Apply filters
    if (this.hideZeroBalances()) {
      rawAssets = rawAssets.filter(a => a.balance > 0);
    }

    // Search query
    if (this.searchQuery()) {
      const q = this.searchQuery().toLowerCase();
      rawAssets = rawAssets.filter(a => a.symbol.toLowerCase().includes(q) || a.name.toLowerCase().includes(q));
    }

    return rawAssets.sort((a, b) => {
      const isNativeA = a.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
      const isNativeB = b.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
      if (isNativeA && !isNativeB) return -1;
      if (!isNativeA && isNativeB) return 1;
      return b.value - a.value;
    });
  });

  currentPage = 1;
  itemsPerPage = 10;

  totalPages = computed(() => {
    return Math.ceil(this.filteredAssets().length / this.itemsPerPage);
  });

  paginatedAssets = computed(() => {
    const assets = this.filteredAssets();
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return assets.slice(startIndex, startIndex + this.itemsPerPage);
  });

  nextPage() {
    if (this.currentPage < this.totalPages()) {
      this.currentPage++;
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }


  positions = signal<any[]>([]);
  isLoadingPositions = false;

  positionsPage = signal(1);
  positionsLimit = 8;
  totalPositionsCount = signal(0);
  
  backendMetrics = signal({ totalLiquidity: 0, totalFarming: 0, totalEarnings: 0 });
  frontendMetrics = signal({ totalLiquidity: 0, totalFarming: 0, totalStaking: 0 });

  activePositions = computed(() => {
    return this.positions().filter(p => !p.isExternal);
  });

  paginatedActivePositions = computed(() => {
    return this.activePositions();
  });

  totalActivePositionsPages = computed(() => {
    return Math.ceil(this.totalPositionsCount() / this.positionsLimit) || 1;
  });

  paginatedPositions = computed(() => {
    return this.positions();
  });

  totalPositionsPages = computed(() => {
    return Math.ceil(this.totalPositionsCount() / this.positionsLimit) || 1;
  });

  nextPositionsPage() {
    const maxPages = this.activeTab === 'NFTs' ? this.totalActivePositionsPages() : this.totalPositionsPages();
    if (this.positionsPage() < maxPages) {
      this.positionsPage.update(v => v + 1);
      this.loadUserPositions();
    }
  }

  prevPositionsPage() {
    if (this.positionsPage() > 1) {
      this.positionsPage.update(v => v - 1);
      this.loadUserPositions();
    }
  }

  overviewMetrics = computed(() => {
    if (!this.appState.isWalletConnected()) {
      return { totalValue: 0, invested: 0, earnings: 0, walletValue: 0, liquidityValue: 0, farmingValue: 0, assetsCount: 0, positionsCount: 0 };
    }

    const tokens = this.appState.tokens();
    const tokenPriceMap: Record<string, number> = {};
    tokens.forEach(t => {
      tokenPriceMap[t.symbol] = t.rate || 0;
    });

    const walletValue = this.filteredAssets().reduce((sum, a) => sum + a.value, 0);
    const assetsCount = this.filteredAssets().length;
    const positionsCount = this.totalPositionsCount();

    // Use frontend metrics instead of backend
    const metrics = this.frontendMetrics();
    const liquidityValue = metrics.totalLiquidity;
    const farmingValue = metrics.totalFarming + metrics.totalStaking;
    
    // Total Earned now comes from the backend's sum of historical claims
    const earnings = this.backendMetrics().totalEarnings;

    const invested = liquidityValue + farmingValue;
    const totalValue = walletValue + invested + earnings;

    return { totalValue, invested, earnings, walletValue, liquidityValue, farmingValue, assetsCount, positionsCount };
  });

  dynamicAllocations = computed(() => {
    const metrics = this.overviewMetrics();
    const total = metrics.totalValue;
    if (total === 0) return [];

    const allocs = [];
    if (metrics.walletValue > 0) {
      allocs.push({ name: 'Wallet', value: metrics.walletValue, percent: Number((metrics.walletValue / total * 100).toFixed(1)), color: '#627eea' });
    }
    if (metrics.liquidityValue > 0) {
      allocs.push({ name: 'Liquidity', value: metrics.liquidityValue, percent: Number((metrics.liquidityValue / total * 100).toFixed(1)), color: '#F5B73B' });
    }
    if (metrics.farmingValue > 0) {
      allocs.push({ name: 'Farming', value: metrics.farmingValue, percent: Number((metrics.farmingValue / total * 100).toFixed(1)), color: '#2775ca' });
    }

    return allocs;
  });

  ngAfterViewInit() {
    if (this.appState.isWalletConnected()) {
      this.initCharts();
    }
  }



  async loadUserPositions() {
    try {
      this.isLoadingPositions = true;
      const typeFilter = this.activeTab === 'NFTs' ? 'internal' : 'all';
      const res: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/liquidity/positions?page=${this.positionsPage()}&limit=${this.positionsLimit}&type=${typeFilter}&chainId=${this.appState.selectedNetworkId()}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`
          }
        })
      );

      if (res.status && res.data) {
        const positionsData = res.data.data || [];
        this.totalPositionsCount.set(res.data.count || 0);
        if (res.data.metrics) {
          this.backendMetrics.set(res.data.metrics);
        }

        console.log('Portfolio -> Paginated positions from backend:', positionsData);
        const uniquePools = [...new Set(positionsData.map((p: any) => p.poolAddress).filter(Boolean))] as string[];
        const livePoolData: Record<string, any> = {};
        
        await Promise.all(uniquePools.map(async (poolAddress) => {
          try {
            const slot0 = await this.web3Service.getExternalPoolSlot0(poolAddress);
            if (slot0) {
              livePoolData[poolAddress] = slot0;
            }
          } catch (e) {
            console.warn('Failed to fetch live slot0 for', poolAddress, e);
          }
        }));

        const mappedPositions = positionsData.map((pos: any) => {
          let t0 = Number(pos.token0Amount || 0);
          let t1 = Number(pos.token1Amount || 0);

          let poolSqrtPriceX96Str = pos.poolSqrtPriceX96?.toString();
          let poolCurrentTick = Number(pos.poolCurrentTick || 0);

          if (pos.poolAddress && livePoolData[pos.poolAddress]) {
             poolSqrtPriceX96Str = livePoolData[pos.poolAddress].sqrtPriceX96.toString();
             poolCurrentTick = Number(livePoolData[pos.poolAddress].tick);
             console.log(`DEBUG [pos ${pos.tokenId}]: Using live slot0 from blockchain -> sqrtPriceX96=${poolSqrtPriceX96Str}`);
          } else {
             console.log(`DEBUG [pos ${pos.tokenId}]: Using fallback slot0 from DB -> sqrtPriceX96=${poolSqrtPriceX96Str}`);
          }

          if (poolSqrtPriceX96Str) {
            try {
              const mockToken0 = new UniToken(1, pos.token0Address || '0x', pos.token0Decimals || 18, 'TK0');
              const mockToken1 = new UniToken(1, pos.token1Address || '0x', pos.token1Decimals || 18, 'TK1');

              const sqrtPriceX96JSBI = JSBI.BigInt(poolSqrtPriceX96Str);
              let computedTick = 0;
              try {
                computedTick = TickMath.getTickAtSqrtRatio(sqrtPriceX96JSBI);
              } catch (err) {
                computedTick = poolCurrentTick;
              }

              const pool = new Pool(
                mockToken0,
                mockToken1,
                Number(pos.poolFee || 3000),
                poolSqrtPriceX96Str,
                1,
                computedTick
              );

              // Use liquidityStr if available, otherwise fallback to parsing the Number (which loses precision)
              let rawLiquidityStr = '0';
              if (pos.liquidityStr && pos.liquidityStr !== '0') {
                rawLiquidityStr = pos.liquidityStr;
              } else {
                rawLiquidityStr = pos.liquidity ? ethers.parseUnits(Number(pos.liquidity).toFixed(18), 18).toString() : '0';
              }

              const position = new Position({
                pool,
                liquidity: rawLiquidityStr,
                tickLower: Number(pos.tickLower),
                tickUpper: Number(pos.tickUpper)
              });

              t0 = Number(position.amount0.toExact());
              t1 = Number(position.amount1.toExact());

              console.log(`DEBUG [pos ${pos.tokenId}]: Live Recalculation SUCCESS -> t0=${t0}, t1=${t1} (sqrtPriceX96=${poolSqrtPriceX96Str}, liquidityStr=${rawLiquidityStr})`);

              // Override static backend amounts with live amounts
              pos.token0Amount = t0;
              pos.token1Amount = t1;
            } catch (e) {
              console.log(`DEBUG [pos ${pos.tokenId}]: Live Recalculation FAILED`, e);
            }
          } else {
            console.log(`DEBUG [pos ${pos.tokenId}]: NO poolSqrtPriceX96Str AVAILABLE!`);
          }

          const feePercent = pos.poolFee ? (Number(pos.poolFee) / 10000) + '%' : '0.3%';

          const t0Dec = pos.token0Decimals || 18;
          const t1Dec = pos.token1Decimals || 18;

          const calculatePrice = (tick: number) => {
             if (tick === undefined || tick === null || isNaN(tick)) return 0;
             return Math.pow(1.0001, tick) * Math.pow(10, t0Dec - t1Dec);
          };

          const minPriceRaw = calculatePrice(Number(pos.tickLower));
          const maxPriceRaw = calculatePrice(Number(pos.tickUpper));
          const formatPrice = (val: number) => {
            if (val === undefined || val === null || isNaN(val)) return '0.00';
            if (val === 0) return '0.00';
            if (val >= 1e9) return (val / 1e9).toFixed(2) + 'B';
            if (val >= 1e6) return (val / 1e6).toFixed(2) + 'M';
            if (val >= 1e3) return (val / 1e3).toFixed(2) + 'K';
            
            let plainStr = formatPlain(val).replace(/0+$/, '');
            const match = plainStr.match(/^0\.0+/);
            if (match) {
              const zeroCount = match[0].length - 2; 
              if (zeroCount >= 5) {
                const subscripts = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
                const countStr = zeroCount.toString().split('').map(d => subscripts[parseInt(d)]).join('');
                const sigDigits = plainStr.slice(match[0].length).slice(0, 4);
                return `0.0${countStr}${sigDigits}`;
              }
            }
            
            return new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 8
            }).format(val);
          };
          const minPrice = formatPrice(minPriceRaw);
          const maxPrice = formatPrice(maxPriceRaw);

          const currentTick = Number(poolCurrentTick) || 0;
          const currentPriceRaw = calculatePrice(currentTick);
          
          const inRange = true;

          const isFullRange = (pos.tickLower !== undefined && pos.tickUpper !== undefined)
              ? (Number(pos.tickLower) <= -887000 && Number(pos.tickUpper) >= 887000)
              : false;

          const token0State = this.appState.tokens().find((t: any) => t.address?.toLowerCase() === pos.token0Address?.toLowerCase());
          const token1State = this.appState.tokens().find((t: any) => t.address?.toLowerCase() === pos.token1Address?.toLowerCase());

          return {
            tokenId: pos.tokenId,
            liquidity: pos.liquidity,
            title: `${pos.token0Symbol} / ${pos.token1Symbol}`,
            ...pos,
            status,
            type: pos.isExternal ? 'External Liquidity' : feePercent,
            fee: feePercent,
            inRange: inRange,
            isFullRange: isFullRange,
            minPrice: minPrice,
            maxPrice: maxPrice,
            minPriceRaw: minPriceRaw,
            maxPriceRaw: maxPriceRaw,
            currentPriceRaw: currentPriceRaw,
            metric1Value: this.formatSubscript(t0),
            metric1Label: pos.token0Symbol || 'T0',
            metric2Value: this.formatSubscript(t1),
            metric2Label: pos.token1Symbol || 'T1',
            icon1: pos.token0Symbol || 'T0',
            icon2: pos.token1Symbol || 'T1',
            color1: (token0State?.logoUrl || pos.token0Image) ? 'transparent' : '#888',
            color2: (token1State?.logoUrl || pos.token1Image) ? 'transparent' : '#888',
            image1: token0State?.logoUrl || pos.token0Image,
            image2: token1State?.logoUrl || pos.token1Image,
            token0Amount: pos.token0Amount || 0,
            token1Amount: pos.token1Amount || 0,
            feeToken0: pos.feeToken0 || 0,
            feeToken1: pos.feeToken1 || 0,
          };
        });
        this.positions.set(mappedPositions);

        if (mappedPositions.length > 0) {
           this.fetchPositionChartData();
        }

        // Global metrics is fetched independently now
      }
    } catch (e) {
      console.error('Failed to load user positions', e);
    } finally {
      this.isLoadingPositions = false;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.initPerformanceChart();
        this.initAllocationChart();
      }, 50);
    }
  }

  fetchPositionChartData() {
    const currentPositions = this.positions();
    for (const pos of currentPositions) {
      if (!pos.poolAddress) continue;
      const url = `${environment.apiUrl}/user/liquidity/pool/${pos.poolAddress}/ohlcv?timeframe=1M`;
      this.http.get<any>(url).subscribe({
        next: (res) => {
          if (res?.data?.attributes?.ohlcv_list) {
            pos.chartData = res.data.attributes.ohlcv_list.reverse();
            this.cdr.detectChanges();
          }
        },
        error: (err) => console.warn('Failed to load chart data for position in portfolio', err)
      });
    }
  }

  async fetchGlobalMetrics() {
    if (!this.appState.isWalletConnected()) return;

    try {
      const chainId = this.appState.selectedNetworkId();
      
      const posRes: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/liquidity/user-positions-all?chainId=${chainId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` }
        })
      );
      
      const stakingRes: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/staking/user-deposits/${this.appState.walletAddress().toLowerCase()}?chainId=${chainId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` }
        })
      );

      let totalLiquidity = 0;
      let totalFarming = 0;
      let totalStaking = 0;

      const tokens = this.appState.tokens();
      const getTokenPrice = (addressOrSymbol: string) => {
         if (!addressOrSymbol) return 0;
         const t = tokens.find(tok => tok.address?.toLowerCase() === addressOrSymbol.toLowerCase() || tok.symbol === addressOrSymbol);
         return t?.rate || 0;
      };

      if (stakingRes.status && stakingRes.data?.data) {
         stakingRes.data.data.forEach((deposit: any) => {
            const price = getTokenPrice(deposit.pool?.stakingTokenAddress || deposit.pool?.tokenName);
            const val = Number(deposit.amount || 0) * price;
            totalStaking += val;
         });
      }

      this.frontendMetrics.update(m => ({ ...m, totalStaking }));

      if (posRes.status && posRes.data) {
        const positionsAll = posRes.data;

        for (const pos of positionsAll) {
          const t0Price = getTokenPrice(pos.token0Address || pos.token0Symbol);
          const t1Price = getTokenPrice(pos.token1Address || pos.token1Symbol);
          
          let t0Amt = Number(pos.token0Amount || 0);
          let t1Amt = Number(pos.token1Amount || 0);
          
          const posValue = (t0Amt * t0Price) + (t1Amt * t1Price);

          if (pos.isStakedInFarm) {
             totalFarming += posValue;
          } else {
             totalLiquidity += posValue;
          }
        }
        
        this.frontendMetrics.update(m => ({ ...m, totalLiquidity, totalFarming }));
      }
    } catch(e) {
      console.error('Failed to fetch global metrics', e);
    }
  }

  ngOnDestroy() {
    if (this.performanceChartInstance) {
      this.performanceChartInstance.dispose();
    }
    if (this.allocationChartInstance) {
      this.allocationChartInstance.dispose();
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.performanceChartInstance) {
      this.performanceChartInstance.resize();
    }
    if (this.allocationChartInstance) {
      this.allocationChartInstance.resize();
    }
  }

  setTimeframe(tf: string) {
    this.activeTimeframe = tf;
    this.initPerformanceChart();
  }

  setTab(tab: string) {
    this.activeTab = tab;
    // Update URL fragment without triggering Angular Router navigation/scrolling
    window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + tab);

    if ((tab === 'Positions' || tab === 'NFTs') && this.authService.isAuthenticated()) {
      this.positionsPage.set(1);
      this.loadUserPositions();
    }
    setTimeout(() => {
      this.initCharts();
    }, 50);
  }
  openRemoveModal(pos: any) {
    this.selectedPosition = pos;
    this.removePercentage = 50;
    this.isRemoveModalOpen = true;
    this.fetchLivePositionData(pos.tokenId);
  }

  closeRemoveModal() {
    this.isRemoveModalOpen = false;
    setTimeout(() => {
      this.selectedPosition = null;
      this.liveFeeAmount0 = '0.00';
      this.liveFeeAmount1 = '0.00';
      this.liveToken0Amount = null;
      this.liveToken1Amount = null;
    }, 300);
  }

  openClaimModal(pos: any) {
    this.selectedPosition = pos;
    this.isClaimModalOpen = true;
    this.fetchLivePositionData(pos.tokenId);
  }

  closeClaimModal() {
    this.isClaimModalOpen = false;
    setTimeout(() => {
      this.selectedPosition = null;
      this.liveFeeAmount0 = '0.00';
      this.liveFeeAmount1 = '0.00';
      this.rawFeeAmount0 = 0;
      this.rawFeeAmount1 = 0;
      this.liveToken0Amount = null;
      this.liveToken1Amount = null;
    }, 300);
  }

  async fetchLivePositionData(tokenId: number) {
    try {
      this.isLoadingLiveFees = true;
      this.cdr.detectChanges();

      let managerAddress: string | undefined = undefined;
      const contracts = this.web3Service.getContractsConfig();
      if (this.selectedPosition?.isExternal) {
        if (this.selectedPosition.managerAddress) {
          managerAddress = this.selectedPosition.managerAddress;
        } else if (this.selectedPosition.platformName === 'Uniswap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        } else if (this.selectedPosition.platformName === 'PancakeSwap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.PancakeSwap || '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364';
        } else {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        }
      }

      const fees = await this.web3Service.getPendingFees(tokenId, managerAddress);
      console.log(`[portfolio.fetchLivePositionData] fees from web3:`, fees);
      
      const dec0 = this.selectedPosition?.token0Decimals || 18;
      const dec1 = this.selectedPosition?.token1Decimals || 18;

      const numAmount0 = Number(ethers.formatUnits(fees.amount0, dec0));
      const numAmount1 = Number(ethers.formatUnits(fees.amount1, dec1));
      this.rawFeeAmount0 = numAmount0;
      this.rawFeeAmount1 = numAmount1;

      const tokens = this.appState.tokens();
      const token0Price = tokens.find(t => t.symbol === this.selectedPosition?.icon1)?.rate || 0;
      const token1Price = tokens.find(t => t.symbol === this.selectedPosition?.icon2)?.rate || 0;

      this.liveFeeUsd0 = numAmount0 * token0Price;
      this.liveFeeUsd1 = numAmount1 * token1Price;
      
      this.liveFeeUsdFormatted0 = this.formatSubscript(this.liveFeeUsd0);
      this.liveFeeUsdFormatted1 = this.formatSubscript(this.liveFeeUsd1);

      console.log(`[portfolio.fetchLivePositionData] formatted amounts (num):`, { numAmount0, numAmount1 });

      this.liveFeeAmount0 = this.formatSubscript(numAmount0);
      this.liveFeeAmount1 = this.formatSubscript(numAmount1);
      console.log(`[portfolio.fetchLivePositionData] liveFeeAmount0:`, this.liveFeeAmount0);
      console.log(`[portfolio.fetchLivePositionData] liveFeeAmount1:`, this.liveFeeAmount1);

      // Resync the main list's unclaimed fees with this freshly fetched live data
      const posToUpdate = this.positions().find((p: any) => p.tokenId === tokenId);
      if (posToUpdate) {
        posToUpdate.feeToken0 = numAmount0;
        posToUpdate.feeToken1 = numAmount1;
        posToUpdate.unclaimedFeesUsd = this.liveFeeUsd0 + this.liveFeeUsd1;
        
        // Portfolio does not display global unclaimed fees, so we just update the specific pos
      }

      if (this.selectedPosition?.poolAddress) {
        let manager;
        if (managerAddress) {
          manager = await this.web3Service.getContract(managerAddress, POSITION_MANAGER_ABI);
        } else {
          manager = await this.web3Service.getPositionManager();
        }

        const posData = await manager['positions'](tokenId);
        const liquidity = posData.liquidity.toString();
        const tickLower = Number(posData.tickLower);
        const tickUpper = Number(posData.tickUpper);
        const fee = Number(posData.fee || 3000);

        const slot0 = await this.web3Service.getExternalPoolSlot0(this.selectedPosition.poolAddress);
        let sqrtPriceX96 = '0';
        let currentTick = 0;
        if (slot0) {
          sqrtPriceX96 = slot0.sqrtPriceX96.toString();
          currentTick = Number(slot0.tick);
        }

        const mockToken0 = new UniToken(1, posData.token0, dec0, 'TK0');
        const mockToken1 = new UniToken(1, posData.token1, dec1, 'TK1');
        const pool = new Pool(
          mockToken0,
          mockToken1,
          fee,
          sqrtPriceX96,
          1,
          currentTick
        );

        const position = new Position({
          pool,
          liquidity,
          tickLower,
          tickUpper
        });

        this.liveToken0Amount = Number(position.amount0.toExact());
        this.liveToken1Amount = Number(position.amount1.toExact());
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoadingLiveFees = false;
      this.cdr.detectChanges();
    }
  }

  async confirmRemoveLiquidity() {
    if (!this.selectedPosition || !this.selectedPosition.tokenId) return;

    try {
      this.isRemovingLiquidity = true;
      console.log('--- START REMOVE LIQUIDITY ---');
      console.log('Selected position:', this.selectedPosition);

      let manager;
      if (this.selectedPosition.isExternal && this.selectedPosition.managerAddress) {
        console.log('Using external manager:', this.selectedPosition.managerAddress);
        manager = await this.web3Service.getContract(this.selectedPosition.managerAddress, POSITION_MANAGER_ABI);
      } else {
        manager = await this.web3Service.getPositionManager();
        console.log('Using default internal manager');
      }

      console.log('Fetching on-chain position data for token ID:', this.selectedPosition.tokenId);
      const posData = await manager['positions'](this.selectedPosition.tokenId);
      const exactBlockchainLiquidity: bigint = posData.liquidity;
      console.log('Exact blockchain liquidity:', exactBlockchainLiquidity.toString());

      if (exactBlockchainLiquidity === 0n) {
        this.appState.triggerNotification('This position is already empty on the blockchain! Updating UI...', false);
        const posIndex = this.positions().findIndex((p: any) => p.tokenId === this.selectedPosition.tokenId);
        if (posIndex !== -1) {
          const newPositions = [...this.positions()];
          newPositions.splice(posIndex, 1);
          this.positions.set(newPositions);
        }
        this.closeRemoveModal();
        return;
      }

      const percentageBigInt = BigInt(this.removePercentage);
      const liquidityToRemove = (exactBlockchainLiquidity * percentageBigInt) / 100n;
      console.log(`Removing ${this.removePercentage}% -> ${liquidityToRemove.toString()}`);

      if (liquidityToRemove === 0n) {
        throw new Error('Liquidity to remove is 0');
      }

      console.log('Calling web3Service.removeLiquidity...');
      const tx = await this.web3Service.removeLiquidity(
        this.selectedPosition.tokenId,
        liquidityToRemove,
        0n,
        0n,
        this.selectedPosition.isExternal ? this.selectedPosition.managerAddress : undefined
      );

      console.log('Transaction generated:', tx.hash);
      this.appState.triggerNotification(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);

      console.log('Waiting for transaction to be mined...');
      await tx.wait();
      console.log('Transaction mined successfully!');
      this.appState.triggerNotification('Liquidity removed successfully!');

      const originalToken0Amount = Number(this.selectedPosition.token0Amount);
      const originalToken1Amount = Number(this.selectedPosition.token1Amount);

      const posIndex = this.positions().findIndex((p: any) => p.tokenId === this.selectedPosition.tokenId);
      if (posIndex > -1) {
        if (this.removePercentage === 100) {
          const newPositions = [...this.positions()];
          newPositions.splice(posIndex, 1);
          this.positions.set(newPositions);
        } else {
          const percentLeft = (100 - this.removePercentage) / 100;
          const newPositions = [...this.positions()];
          const pos = newPositions[posIndex];
          pos.liquidity = Number(pos.liquidity) * percentLeft;
          pos.token0Amount = Number(pos.token0Amount) * percentLeft;
          pos.token1Amount = Number(pos.token1Amount) * percentLeft;
          pos.metric1Value = this.formatSubscript(Number(pos.token0Amount));
          pos.metric2Value = this.formatSubscript(Number(pos.token1Amount));
          pos.value = `Liq: ${Number(pos.liquidity).toFixed(0)}`;
          this.positions.set(newPositions);
        }
      }

      const tokenId = this.selectedPosition.tokenId;
      const positionToLog = { ...this.selectedPosition };
      this.closeRemoveModal();

      try {
        await lastValueFrom(this.http.post(
          `${environment.apiUrl}/user/liquidity/user-positions/sync?chainId=${this.appState.selectedNetworkId()}`,
          { tokenId, action: 'remove', percentage: this.removePercentage, networkId: this.appState.selectedNetworkId() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
        ));

        const logPayload = {
          walletAddress: this.web3Service.address(),
          txHash: tx.hash,
          token0Address: positionToLog.token0Address,
          token0Symbol: positionToLog.token0Symbol,
          token1Address: positionToLog.token1Address,
          token1Symbol: positionToLog.token1Symbol,
          amount0: originalToken0Amount * (this.removePercentage / 100),
          amount1: originalToken1Amount * (this.removePercentage / 100),
          usdValue: 0,
          feeEarned: 0,
          action: 'Remove',
          liquidityType: positionToLog.isExternal ? 'EXTERNAL' : 'INTERNAL',
          platformName: positionToLog.isExternal ? 'External Aggregator' : 'DEX Native'
        };
        try {
          await lastValueFrom(this.http.post(`${environment.apiUrl}/user/liquidity/log`, logPayload, {
            headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` }
          }));
        } catch (e) {
          console.error('Failed to log liquidity removal', e);
        }

        // Unified Activity Log
        await lastValueFrom(this.activityService.recordActivity({
          txHash: tx.hash,
          chainId: this.appState.selectedNetworkId(),
          actionType: 'REMOVE_LIQUIDITY',
          status: 'Success',
          payload: {
            poolAddress: positionToLog.poolAddress,
            percentage: this.removePercentage,
            amount0: originalToken0Amount * (this.removePercentage / 100),
            amount1: originalToken1Amount * (this.removePercentage / 100),
            token0Symbol: positionToLog.token0Symbol,
            token1Symbol: positionToLog.token1Symbol
          },
          platformName: positionToLog.isExternal ? 'External Aggregator' : 'DEX Native'
        }));
      } catch (err) {
        console.error('Backend sync failed', err);
      }
    } catch (err: any) {
      console.error(err);
      if (err?.code === 'ACTION_REJECTED' || err?.message?.includes('User denied') || err?.message?.includes('user rejected')) {
        this.appState.triggerNotification('Transaction rejected by user');
      } else {
        this.appState.triggerNotification(this.appState.handleError(err, 'Transaction failed'));
      }
    } finally {
      this.isRemovingLiquidity = false;
      this.cdr.markForCheck();
    }
  }

  async confirmClaimFees() {
    if (!this.selectedPosition || !this.selectedPosition.tokenId) return;

    try {
      this.isClaimingFees = true;
      let managerAddress: string | undefined = undefined;
      const contracts = this.web3Service.getContractsConfig();
      if (this.selectedPosition?.isExternal) {
        if (this.selectedPosition.managerAddress) {
          managerAddress = this.selectedPosition.managerAddress;
        } else if (this.selectedPosition.platformName === 'Uniswap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        } else if (this.selectedPosition.platformName === 'PancakeSwap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.PancakeSwap || '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364';
        } else {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        }
      }
      
      const tx = await this.web3Service.claimFees(this.selectedPosition.tokenId, managerAddress);
      this.appState.triggerNotification(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      this.appState.triggerNotification('Fees claimed successfully!');

      // Optimistic update
      const posIndex = this.positions().findIndex((p: any) => p.tokenId === this.selectedPosition.tokenId);
      if (posIndex !== -1) {
        const newPositions = [...this.positions()];
        newPositions[posIndex].feeToken0 = 0;
        newPositions[posIndex].feeToken1 = 0;
        this.positions.set(newPositions);
      }

      // Instantly tell the backend to clear fees in the database
      const tokenId = this.selectedPosition.tokenId;
      try {
        await lastValueFrom(this.http.post(
          `${environment.apiUrl}/user/liquidity/user-positions/sync?chainId=${this.appState.selectedNetworkId()}`,
          { tokenId, action: 'claim', networkId: this.appState.selectedNetworkId() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
        ));

        // Unified Activity Log
        await lastValueFrom(this.activityService.recordActivity({
          txHash: tx.hash,
          chainId: this.appState.selectedNetworkId(),
          actionType: 'CLAIM_FEE',
          status: 'Success',
          payload: {
            poolAddress: this.selectedPosition.poolAddress,
            token0Symbol: this.selectedPosition.token0Symbol,
            token1Symbol: this.selectedPosition.token1Symbol,
            feeToken0: this.rawFeeAmount0,
            feeToken1: this.rawFeeAmount1
          },
          platformName: this.selectedPosition.isExternal ? 'External Aggregator' : 'DEX Native'
        }));
      } catch (err) {
        console.error('Backend sync failed', err);
      }

      this.closeClaimModal();
      // We rely completely on the optimistic update for a snappy UX
    } catch (err: any) {
      console.error(err);
      if (err?.code === 'ACTION_REJECTED' || err?.message?.includes('User denied') || err?.message?.includes('user rejected')) {
        this.appState.triggerNotification('Transaction rejected by user');
      } else {
        this.appState.triggerNotification(this.appState.handleError(err, 'Transaction failed'));
      }
    } finally {
      this.isClaimingFees = false;
      this.cdr.markForCheck();
    }
  }

  private initCharts() {
    if (this.performanceChartRef?.nativeElement) {
      this.performanceChartInstance = echarts.init(this.performanceChartRef.nativeElement);
      this.initPerformanceChart();
    }


    if (this.allocationChartRef?.nativeElement) {
      this.allocationChartInstance = echarts.init(this.allocationChartRef.nativeElement);
      this.initAllocationChart();
    }
  }

  async initPerformanceChart() {
    if (!this.performanceChartRef?.nativeElement) return;

    if (!this.performanceChartInstance) {
      this.performanceChartInstance = echarts.init(this.performanceChartRef.nativeElement);
    }

    const currentTotal = this.overviewMetrics().totalValue || 0;
    const userAddress = this.web3Service.address();
    const chainId = this.web3Service.chainId() || 1;

    // The backend cron job now handles recording snapshots automatically.

    // Fetch the real historical snapshots
    let snapshots: any[] = [];
    if (userAddress) {
      try {
        const res: any = await firstValueFrom(this.http.get(`${environment.apiUrl}/user/portfolio/snapshots/${userAddress}?chainId=${chainId}`));
        if (res && res.status && res.data) {
          snapshots = res.data;
        }
      } catch (e) {
        console.error('Failed to fetch portfolio snapshots', e);
      }
    }

    // Ensure we have something to plot
    if (snapshots.length === 0) {
      snapshots = [
        { timestamp: new Date().toISOString(), totalValue: currentTotal }
      ];
    }

    // Filter by timeframe based on hourly snapshots
    let hoursToShow = 30 * 24;
    if (this.activeTimeframe === '7D') hoursToShow = 7 * 24;
    else if (this.activeTimeframe === '30D') hoursToShow = 30 * 24;
    else if (this.activeTimeframe === '90D') hoursToShow = 90 * 24;
    else if (this.activeTimeframe === '1Y') hoursToShow = 365 * 24;
    else if (this.activeTimeframe === 'All') hoursToShow = 1000 * 24;

    const cutoffTime = new Date().getTime() - (hoursToShow * 60 * 60 * 1000);
    const filteredSnapshots = snapshots.filter((s: any) => new Date(s.timestamp).getTime() >= cutoffTime);

    const dates = filteredSnapshots.map((s: any) => {
      const d = new Date(s.timestamp);
      const hrs = d.getHours().toString().padStart(2, '0');
      return `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}, ${hrs}:00`;
    });

    const data = filteredSnapshots.map((s: any) => s.totalValue);

    // Echarts line chart looks bad with a single point. If only one point exists, duplicate it so it draws a flat line
    if (data.length === 1) {
      const d = new Date(filteredSnapshots[0].timestamp);
      d.setHours(d.getHours() - 1);
      const hrs = d.getHours().toString().padStart(2, '0');
      dates.unshift(`${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}, ${hrs}:00`);
      data.unshift(0); // Show it growing from 0
    }

    const option = {
      grid: {
        top: 20,
        bottom: 20,
        left: 0,
        right: 0,
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#111',
        borderColor: '#333',
        textStyle: { color: '#fff' },
        formatter: (params: any) => {
          const p = params[0];
          return `
            <div style="font-weight: 900; margin-bottom: 4px; color: #888; font-size: 11px;">${params[0].axisValue}</div>
            <div style="font-weight: 900; color: #fff; font-size: 14px;">$${Number(params[0].data).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          `;
        }
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#888',
          fontSize: 10,
          margin: 15,
          showMaxLabel: true
        }
      },
      yAxis: {
        type: 'value',
        splitLine: {
          lineStyle: { color: 'rgba(255, 255, 255, 0.05)', type: 'dashed' }
        },
        axisLabel: {
          color: '#888',
          fontSize: 10,
          formatter: (value: number) => {
            if (value >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'M';
            if (value >= 1000) return '$' + (value / 1000).toFixed(1) + 'K';
            return '$' + value.toFixed(0);
          }
        },
        min: 'dataMin'
      },
      series: [
        {
          data: data,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: '#F5B73B',
            width: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(245, 183, 59, 0.3)' },
              { offset: 1, color: 'rgba(245, 183, 59, 0)' }
            ])
          }
        }
      ]
    };

    this.performanceChartInstance.setOption(option);
  }

  private initAllocationChart() {
    if (!this.allocationChartInstance) return;

    const data = this.dynamicAllocations().map(a => ({
      value: a.value,
      name: a.name,
      itemStyle: { color: a.color }
    }));

    const option = {
      tooltip: {
        trigger: 'item',
        backgroundColor: '#111',
        borderColor: '#333',
        textStyle: { color: '#fff' },
        formatter: '{b}: ${c} ({d}%)'
      },
      series: [
        {
          type: 'pie',
          radius: ['60%', '90%'],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          data: data,
          itemStyle: {
            borderWidth: 2,
            borderColor: '#0c0c0c'
          }
        }
      ]
    };

    this.allocationChartInstance.setOption(option);
  }

  // --- Unstake & Rewards ---

  async openUnstakeModal(position: any) {
    this.selectedPosition = position;
    this.isUnstakeModalOpen = true;

    if (position.farmAddress) {
      this.isLoadingPid = true;
      try {
        this.selectedFarmPid = await this.web3Service.getFarmPid(position.farmAddress);
      } catch (e) {
        console.error('Error fetching farm pid', e);
      } finally {
        this.isLoadingPid = false;
      }
    }
  }

  closeUnstakeModal() {
    this.isUnstakeModalOpen = false;
    setTimeout(() => {
      this.selectedPosition = null;
      this.selectedFarmPid = null;
    }, 300);
  }

  async openClaimRewardModal(position: any) {
    console.log('Opening claim reward modal for position:', position);
    this.selectedPosition = position;
    this.isClaimRewardModalOpen = true;

    if (position.farmAddress) {
      console.log('Position has farmAddress:', position.farmAddress);
      this.selectedFarmPid = await this.web3Service.getFarmPid(position.farmAddress);
      console.log('Fetched selectedFarmPid:', this.selectedFarmPid);
      if (this.selectedFarmPid !== null) {
        this.fetchLiveRewards();
      } else {
        console.log('selectedFarmPid is null! Cannot fetch live rewards.');
      }
    } else {
      console.log('Position has no farmAddress! Cannot fetch live rewards.');
    }
  }

  closeClaimRewardModal() {
    this.isClaimRewardModalOpen = false;
    setTimeout(() => {
      this.selectedPosition = null;
      this.selectedFarmPid = null;
      this.liveRewardAmount = '0.00';
    }, 300);
  }

  async fetchLiveRewards() {
    if (this.selectedFarmPid === null) {
      console.log('fetchLiveRewards aborted: selectedFarmPid is null');
      return;
    }
    try {
      this.isLoadingLiveReward = true;
      this.cdr.markForCheck();
      console.log('Fetching pending reward for pid:', this.selectedFarmPid);
      const pending = await this.web3Service.getPendingReward(this.selectedFarmPid);
      console.log('Raw pending reward from blockchain:', pending.toString());
      this.liveRewardAmount = this.formatSubscript(Number(ethers.formatUnits(pending, 18)));
      console.log('Formatted liveRewardAmount:', this.liveRewardAmount);
    } catch (e) {
      console.error('Error in fetchLiveRewards:', e);
    } finally {
      this.isLoadingLiveReward = false;
      this.cdr.markForCheck();
    }
  }

  async confirmUnstake() {
    if (!this.selectedPosition) return;
    if (this.selectedFarmPid === null) {
      alert('Error: Could not find Farm PID for this position.');
      return;
    }

    try {
      this.isUnstaking = true;

      // Fetch pending rewards before unstaking
      const pending = await this.web3Service.getPendingReward(this.selectedFarmPid);
      const claimedRewardAmount = ethers.formatUnits(pending, 18).toString();

      const tx = await this.web3Service.unstake(this.selectedFarmPid, this.selectedPosition.tokenId);
      this.appState.triggerNotification(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      this.appState.triggerNotification('NFT Unstaked successfully!');

      // Optimistic update
      const posIndex = this.positions().findIndex((p: any) => p.tokenId === this.selectedPosition.tokenId);
      if (posIndex !== -1) {
        const newPositions = [...this.positions()];
        newPositions[posIndex].isStakedInFarm = false;
        newPositions[posIndex].type = 'Liquidity';
        this.positions.set(newPositions);
      }
      const tokenId = this.selectedPosition.tokenId;
      this.closeUnstakeModal();

      try {
        await lastValueFrom(this.http.post(
          `${environment.apiUrl}/user/liquidity/user-positions/sync?chainId=${this.appState.selectedNetworkId()}`,
          { tokenId, action: 'unstake', networkId: this.appState.selectedNetworkId() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
        ));

        // Unified Activity Log
        await lastValueFrom(this.activityService.recordActivity({
          txHash: tx.hash,
          chainId: this.appState.selectedNetworkId(),
          actionType: 'FARM_UNSTAKE',
          status: 'Success',
          payload: {
            tokenId: this.selectedPosition.tokenId,
            poolAddress: this.selectedPosition.poolAddress
          },
          platformName: 'DEX Farming'
        }));

        if (Number(claimedRewardAmount) > 0) {
          const chainId = this.appState.selectedNetworkId();
          const contracts = environment.contracts as any;
          const dxorToken = contracts[chainId]?.TOKENS?.DXOR || '';

          await lastValueFrom(this.activityService.recordActivity({
            txHash: tx.hash,
            chainId: chainId,
            actionType: 'FARM_CLAIM',
            status: 'Success',
            payload: {
              farmPid: this.selectedFarmPid,
              tokenId: this.selectedPosition.tokenId,
              poolAddress: this.selectedPosition.poolAddress,
              rewardAmount: ethers.parseUnits(Number(claimedRewardAmount).toFixed(6), 18).toString(),
              tokenAddress: dxorToken
            },
            platformName: 'DEX Farming'
          }));
        }
      } catch (err) {
        console.error('Backend sync failed', err);
      }

      setTimeout(() => {
        this.loadUserPositions();
      }, 3000);
    } catch (err: any) {
      console.error(err);
      this.appState.triggerNotification(this.appState.handleError(err, 'Unstake failed'));
    } finally {
      this.isUnstaking = false;
      this.cdr.markForCheck();
    }
  }

  async confirmClaimReward() {
    if (!this.selectedPosition || this.selectedFarmPid === null) return;
    try {
      this.isClaimingReward = true;
      const tx = await this.web3Service.claimReward(this.selectedFarmPid);
      this.appState.triggerNotification(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      this.appState.triggerNotification('Rewards claimed successfully!');

      const claimedRewardAmount = this.liveRewardAmount;
      this.liveRewardAmount = '0.00';
      this.closeClaimRewardModal();

      const chainId = this.appState.selectedNetworkId();
      const contracts = environment.contracts as any;
      const dxorToken = contracts[chainId]?.TOKENS?.DXOR || '';

      await lastValueFrom(this.activityService.recordActivity({
        txHash: tx.hash,
        chainId: chainId,
        actionType: 'FARM_CLAIM',
        status: 'Success',
        payload: {
          farmPid: this.selectedFarmPid,
          tokenId: this.selectedPosition.tokenId,
          poolAddress: this.selectedPosition.poolAddress,
          rewardAmount: ethers.parseUnits(claimedRewardAmount, 18).toString(),
          tokenAddress: dxorToken
        },
        platformName: 'DEX Farming'
      }));
    } catch (err: any) {
      console.error(err);
      this.appState.triggerNotification(this.appState.handleError(err, 'Claim rewards failed'));
    } finally {
      this.isClaimingReward = false;
      this.cdr.markForCheck();
    }
  }
}
